import { meshCoverageRuntime } from '../core/mesh-coverage-runtime';
import { noiseLayerRuntime } from '../core/noise-layer-runtime';
import { lastAllowedSelectionKey } from '../core/layer-selection';
import {
    BDOT10K_IMPACT_CLASSIFICATION_PARAM,
    BDOT10K_NUISANCE_LEVEL_PARAM,
    BDOT10K_USEFULNESS_LEVEL_PARAM,
    BASE_URL,
    DEFAULT_GUNB_ROK_WPLYWU_OD,
    DEFAULT_LAT,
    DEFAULT_LNG,
    DEFAULT_SORT,
    DEFAULT_TRANSACTION_ROK_OD,
    DEFAULT_ZOOM,
    DZIALKA_ID_PARAM,
    GUNB_EMPTY_FILTER_URL_VALUE,
    GUNB_ROK_WPLYWU_OD_PARAM,
    PARCEL_RENDER_MODE,
    PERF_DEBUG,
    PRG_GEOMETRY_URL,
    TRANSACTION_ROK_OD_PARAM,
    VALID_ASSETS,
    VALID_SORTS,
    VALID_TABS,
    YEAR_FILTER_EMPTY_URL_VALUE,
} from '../core/constants';
import {
    addressInput,
    addressRadiusKmInput,
    addressSuggestEl,
    assetEls,
    bdot10kImpactClassificationInput,
    bdot10kNuisanceLevelInput,
    bdot10kUsefulnessLevelInput,
    dzialkaIdInput,
    gunbRokWplywuOdInput,
    listingAdvertiserTypeInput,
    listingAreaMaxInput,
    listingAreaMinInput,
    listingBuildYearMaxInput,
    listingBuildYearMinInput,
    listingBuildingFloorsMaxInput,
    listingBuildingFloorsMinInput,
    listingBuildingMaterialInput,
    listingBuildingTypeInput,
    listingCategoryInput,
    listingConditionInput,
    listingCreatedDaysInput,
    listingDepositMaxInput,
    listingDescriptionQueryInput,
    listingEnergyClassInput,
    listingFeaturesInput,
    listingFloorInput,
    listingFurnishedInput,
    listingHasPhotosInput,
    listingHeatingTypeInput,
    listingLocationQueryInput,
    listingMarketTypeInput,
    listingMpzpInput,
    listingOfferTypeInput,
    listingOwnershipTypeInput,
    listingParkingInput,
    listingPetPolicyInput,
    listingPlotZoningInput,
    listingPricePerM2MaxInput,
    listingPricePerM2MinInput,
    listingPriceMaxInput,
    listingPriceMinInput,
    listingRadiusKmInput,
    listingRentalTermInput,
    listingRoadAccessInput,
    listingRoomsInput,
    rodzajInput,
    rokDoInput,
    rokOdInput,
    rynekInput,
    sortEls,
    terytInput,
    wojInput,
} from '../dom/elements';
import { qstr } from '../utils/url';
import { kowrLayerRuntime } from '../core/kowr-layer-runtime';
import { educationLayerRuntime } from '../core/education-layer-runtime';
import { ownershipLayerRuntime } from '../core/ownership-layer-runtime';
import { landUseLayerRuntime } from '../core/land-use-layer-runtime';
import {
    encodeParcelMeasurementsState,
    parseParcelMeasurementsState,
} from '../utils/parcel-measurements-url';

var VALID_SIGNAL_TYPES = ['dzialka', 'budynek', 'miejscowosc', 'punkt'];
var VALID_ADDRESS_REF_TYPES = ['locality', 'street', 'place', 'address', 'bdot_area'];
var VALID_LISTING_LOCATION_REF_TYPES = VALID_ADDRESS_REF_TYPES.concat(['gmina']);
// PRG code of an area page (/obszar/…): województwo, powiat, gmina, jednostka
// ewidencyjna or obręb (TransakcjeFilterHelper::PARAM_OBSZAR).
var AREA_CODE_PATTERN = /^(\d{2}|\d{4}|\d{7}|\d{6}_\d|\d{6}_\d\.\d{4})$/;
var PARCEL_MEASUREMENTS_PARAM = 'pm';

export function createDefaultFilterState() {
    return {
        address_query: '',
        address_radius_km: '',
        address_ref_type: '',
        address_ref_id: '',
        address_ref_lat: '',
        address_ref_lng: '',
        address_ref_bbox: '',
        woj: '',
        teryt: '',
        obszar: '',
        tran_rodzaj_trans: '',
        rodzaj_rynku: '',
        rok_od: DEFAULT_TRANSACTION_ROK_OD,
        rok_do: '',
        gunb_rok_wplywu_od: DEFAULT_GUNB_ROK_WPLYWU_OD,
        dzialka_id: '',
        listing_category: 'plot',
        listing_offer_type: '',
        listing_location_query: '',
        listing_location_ref_type: '',
        listing_location_ref_id: '',
        listing_location_ref_lat: '',
        listing_location_ref_lng: '',
        listing_location_ref_bbox: '',
        listing_radius_km: '',
        listing_market_type: '',
        listing_advertiser_type: '',
        listing_price_min: '',
        listing_price_max: '',
        listing_price_per_m2_min: '',
        listing_price_per_m2_max: '',
        listing_area_min: '',
        listing_area_max: '',
        listing_rooms: [],
        listing_floor: [],
        listing_build_year_min: '',
        listing_build_year_max: '',
        listing_building_floors_min: '',
        listing_building_floors_max: '',
        listing_building_type: [],
        listing_building_material: [],
        listing_features: [],
        listing_has_photos: '',
        listing_ownership_type: '',
        listing_condition: '',
        listing_heating_type: '',
        listing_energy_class: '',
        listing_parking: '',
        listing_rental_term: '',
        listing_furnished: '',
        listing_deposit_max: '',
        listing_pet_policy: '',
        listing_created_days: '',
        listing_description_query: '',
        listing_plot_zoning: '',
        listing_road_access: '',
        listing_mpzp: '',
        bdot10k_impact_classification: '',
        bdot10k_nuisance_level_min: '',
        bdot10k_usefulness_level_min: ''
    };
}

function normalizeBdot10kImpactClassification(value) {
    var normalized = value == null ? '' : String(value).trim();
    if (['harmful', 'neutral', 'useful'].indexOf(normalized) === -1) {
        return '';
    }
    return normalized;
}

function normalizeBdot10kScore(value) {
    var normalized = value == null ? '' : String(value).trim();
    if (!normalized) {
        return '';
    }

    var parsed = parseInt(normalized, 10);
    if (!isFinite(parsed) || parsed <= 0) {
        return '';
    }

    return String(Math.max(1, Math.min(5, parsed)));
}

function restoreSignalContextFromUrl(params) {
    var entityType = params.get('signal_type') || '';
    var entityId = params.get('signal_entity_id') || '';

    if (VALID_SIGNAL_TYPES.indexOf(entityType) === -1 || entityId === '') {
        return null;
    }

    return {
        entityType: entityType,
        entityId: entityId,
        targetIdentifier: params.get('signal_target_identifier') || null,
        targetLabel: params.get('signal_target_label') || null,
        targetSubtitle: params.get('signal_target_subtitle') || null,
        selectedSignalId: params.get('signal_id') || null,
        // signal_type/signal_entity_id towarzyszą każdej zaznaczonej działce, więc
        // dopiero signal_id świadczy o tym, że wybrany był konkretny sygnał.
        signalFirst: !!params.get('signal_id'),
    };
}

export function restoreNoiseContextFromUrl(params) {
    var noiseHitId = params.get('noise_hit') || '';
    var noiseHitLat = parseFloat(params.get('noise_hit_lat'));
    var noiseHitLng = parseFloat(params.get('noise_hit_lng'));
    var noiseHitLevel = parseFloat(params.get('noise_hit_level'));
    var noiseHitLayerKey = noiseHitId.split(':', 1)[0];

    if (['noiseLdwn', 'noiseLn', 'noiseMonitoring'].indexOf(noiseHitLayerKey) === -1
        || noiseHitId.indexOf(':') <= 0
        || !isFinite(noiseHitLat)
        || !isFinite(noiseHitLng)) {
        return null;
    }

    return {
        id: noiseHitId.slice(0, 200),
        layerKey: noiseHitLayerKey,
        latlng: { lat: noiseHitLat, lng: noiseHitLng },
        level: isFinite(noiseHitLevel) ? noiseHitLevel : null,
    };
}

export function normalizeTransactionRokOd(value) {
    var normalized = value == null ? '' : String(value).trim();
    if (!normalized || normalized === YEAR_FILTER_EMPTY_URL_VALUE) {
        return '';
    }

    var parsed = parseInt(normalized, 10);
    return isFinite(parsed) ? String(parsed) : '';
}

export function parseTransactionRokOdFromUrl(value) {
    if (value === null) {
        return DEFAULT_TRANSACTION_ROK_OD;
    }

    return normalizeTransactionRokOd(value);
}

export function normalizeGunbRokWplywuOd(value) {
    var normalized = value == null ? '' : String(value).trim();
    if (!normalized || normalized === GUNB_EMPTY_FILTER_URL_VALUE) {
        return '';
    }

    var parsed = parseInt(normalized, 10);
    return isFinite(parsed) ? String(parsed) : '';
}

export function parseGunbRokWplywuOdFromUrl(value) {
    if (value === null) {
        return DEFAULT_GUNB_ROK_WPLYWU_OD;
    }

    return normalizeGunbRokWplywuOd(value);
}

export function getGunbFilterParams(filterState) {
    return {
        [GUNB_ROK_WPLYWU_OD_PARAM]: filterState.gunb_rok_wplywu_od || GUNB_EMPTY_FILTER_URL_VALUE,
    };
}

export function getGunbFilterQueryString(baseParams, filterState) {
    return qstr(Object.assign({}, baseParams || {}, getGunbFilterParams(filterState)));
}

export function getBdot10kFilterParams(filterState) {
    var filters = {};

    if (filterState.bdot10k_impact_classification) {
        filters[BDOT10K_IMPACT_CLASSIFICATION_PARAM] = filterState.bdot10k_impact_classification;
    }
    if (filterState.bdot10k_nuisance_level_min) {
        filters[BDOT10K_NUISANCE_LEVEL_PARAM] = filterState.bdot10k_nuisance_level_min;
    }
    if (filterState.bdot10k_usefulness_level_min) {
        filters[BDOT10K_USEFULNESS_LEVEL_PARAM] = filterState.bdot10k_usefulness_level_min;
    }

    return filters;
}

export function getBdot10kFilterQueryString(baseParams, filterState) {
    return qstr(Object.assign({}, baseParams || {}, getBdot10kFilterParams(filterState)));
}

export function normalizeAddress(value) {
    return value ? String(value).replace(/\s+/g, ' ').trim() : '';
}

function normalizeNonNegativeNumber(value) {
    var normalized = String(value == null ? '' : value).replace(',', '.').trim();
    if (!normalized) return '';
    var parsed = Number(normalized);
    return isFinite(parsed) && parsed >= 0 ? normalized : '';
}

function normalizeRadiusKm(value) {
    var normalized = normalizeNonNegativeNumber(value);
    if (!normalized) return '';
    var parsed = Number(normalized);
    if (!isFinite(parsed) || parsed <= 0) return '';
    return String(Math.min(30, parsed));
}

function syncInputValue(input, value) {
    if (input && input.value !== value) {
        input.value = value;
    }
}

export function normalizeAreaCode(value) {
    var normalized = value == null ? '' : String(value).trim();
    return AREA_CODE_PATTERN.test(normalized) ? normalized : '';
}

function normalizeAddressRefType(value) {
    var normalized = value == null ? '' : String(value).trim();
    return VALID_ADDRESS_REF_TYPES.indexOf(normalized) !== -1 ? normalized : '';
}

export function isListingAreaReference(type) {
    return type === 'bdot_area' || type === 'gmina';
}

export async function restoreListingAreaBbox(filterState, fetchGeometry = fetch) {
    var type = filterState.listing_location_ref_type;
    var id = filterState.listing_location_ref_id;
    if (!isListingAreaReference(type) || !id || filterState.listing_location_ref_bbox) return false;

    try {
        var response = await fetchGeometry(PRG_GEOMETRY_URL + '?type=' + encodeURIComponent(type) + '&id=' + encodeURIComponent(id));
        if (!response.ok) return false;
        var data = await response.json();
        var bbox = data && data.ok === true && Array.isArray(data.bbox)
            ? normalizeBbox(data.bbox.join(',')) : '';
        if (!bbox || filterState.listing_location_ref_type !== type || filterState.listing_location_ref_id !== id
            || filterState.listing_location_ref_bbox) return false;
        filterState.listing_location_ref_bbox = bbox;
        return true;
    } catch (_) {
        return false;
    }
}

function normalizeListingLocationRefType(value) {
    var normalized = value == null ? '' : String(value).trim();
    return VALID_LISTING_LOCATION_REF_TYPES.indexOf(normalized) !== -1 ? normalized : '';
}

function normalizeCoordinate(value) {
    var normalized = value == null ? '' : String(value).trim();
    if (!normalized) return '';
    var parsed = Number(normalized);
    return isFinite(parsed) ? normalized : '';
}

function normalizeBbox(value) {
    var normalized = value == null ? '' : String(value).trim();
    if (!normalized) return '';
    var parts = normalized.split(',').map(Number);
    if (parts.length !== 4 || !parts.every(isFinite)) return '';
    if (parts[0] >= parts[2] || parts[1] >= parts[3]) return '';
    return parts.join(',');
}

function selectedOptions(selectEl) {
    if (!selectEl) return [];
    if (selectEl.options) {
        return Array.prototype.slice.call(selectEl.options)
            .filter(function(option) { return option.selected && option.value !== ''; })
            .map(function(option) { return option.value; });
    }
    return Array.prototype.slice.call(selectEl)
        .filter(function(input) { return input.checked && input.value !== ''; })
        .map(function(input) { return input.value; });
}

function setSelectedOptions(selectEl, values) {
    if (!selectEl) return;
    var selected = (values || []).map(String);
    if (selectEl.options) {
        Array.prototype.slice.call(selectEl.options).forEach(function(option) {
            option.selected = selected.indexOf(String(option.value)) !== -1;
        });
        return;
    }
    Array.prototype.slice.call(selectEl).forEach(function(input) {
        input.checked = selected.indexOf(String(input.value)) !== -1;
    });
}

export function sortField(sort) {
    return sort && sort.charAt(0) === '-' ? sort.slice(1) : sort;
}

export function sortDir(sort) {
    return sort && sort.charAt(0) === '-' ? 'ASC' : 'DESC';
}

export function restoreStateFromUrl() {
    var params = new URLSearchParams(location.search);
    var lat = parseFloat(params.get('lat'));
    var lng = parseFloat(params.get('lng'));
    var zoom = parseFloat(params.get('zoom'));

    var currentAssetType = VALID_ASSETS.indexOf(params.get('asset')) !== -1 ? params.get('asset') : 'dzialki';
    var rawSort = params.get('sort') || '';
    var currentSort = VALID_SORTS.indexOf(sortField(rawSort)) !== -1 ? rawSort : DEFAULT_SORT;
    var activeTab = VALID_TABS.indexOf(params.get('tab')) !== -1 ? params.get('tab') : 'list';
    var selectedTid = params.get('tid') || null;
    var selectedDid = params.get('did') || null;
    var selectedBid = params.get('bid') || null;
    var selectedGunbId = params.get('gid') ? parseInt(params.get('gid'), 10) : null;
    var selectedListingId = params.get('listing_id') ? parseInt(params.get('listing_id'), 10) : null;
    // Deep links from investment pages (DeveloperOffersController::mapUrl) and
    // a selected investment carry its identifier.
    var selectedDeveloperInvestmentId = parseInt(params.get('developer_offer_investment_id') || '', 10);
    selectedDeveloperInvestmentId = selectedDeveloperInvestmentId > 0 ? selectedDeveloperInvestmentId : null;
    var selectedOtoczenieId = params.get('otoczenie_id') || null;
    var selectedNoiseContext = restoreNoiseContextFromUrl(params);
    var browseLat = parseFloat(params.get('browse_lat'));
    var browseLng = parseFloat(params.get('browse_lng'));
    var browseLatlng = isFinite(browseLat) && isFinite(browseLng)
        ? { lat: browseLat, lng: browseLng }
        : null;
    var openBdotId = params.get('open_bdot') || null;
    var activeNearbyId = params.get('nearby_id') || openBdotId || null;
    var pendingNearbyRestore = params.get('nearby') === '1' || activeTab === 'nearby' || !!activeNearbyId;
    var nearbyLoaded = false;
    var selectedSignalContext = restoreSignalContextFromUrl(params);
    var nearbyLat = parseFloat(params.get('nearby_lat'));
    var nearbyLng = parseFloat(params.get('nearby_lng'));
    if (openBdotId && activeTab === 'list') {
        activeTab = 'nearby';
    }

    var urlLayers = (params.get('layers') || '').split(',');
    var selectedParcelOverlay = lastAllowedSelectionKey(['ownership', 'land-use'], urlLayers);
    kowrLayerRuntime.setDesired(urlLayers.indexOf('kowr') !== -1);
    educationLayerRuntime.setDesired(urlLayers.indexOf('szkoly') !== -1);
    ownershipLayerRuntime.setDesired(selectedParcelOverlay === 'ownership');
    landUseLayerRuntime.setDesired(selectedParcelOverlay === 'land-use');
    meshCoverageRuntime.setDesired(urlLayers.indexOf('widoki3d') !== -1);
    var transakcjeDesired = urlLayers.indexOf('no-transakcje') === -1;
    var transakcjeActive = transakcjeDesired;
    if (!transakcjeDesired) {
        selectedTid = null;
    }

    var filterState = createDefaultFilterState();
    filterState.address_query = params.get('address_query') || '';
    filterState.address_radius_km = normalizeRadiusKm(params.get('address_radius_km') || '');
    filterState.address_ref_type = normalizeAddressRefType(params.get('address_ref_type') || '');
    filterState.address_ref_id = params.get('address_ref_id') || '';
    filterState.address_ref_lat = normalizeCoordinate(params.get('address_ref_lat') || '');
    filterState.address_ref_lng = normalizeCoordinate(params.get('address_ref_lng') || '');
    filterState.address_ref_bbox = normalizeBbox(params.get('address_ref_bbox') || '');
    filterState.woj = params.get('woj') || '';
    filterState.teryt = params.get('teryt') || '';
    filterState.obszar = normalizeAreaCode(params.get('obszar'));
    filterState.tran_rodzaj_trans = params.get('tran_rodzaj_trans') || '';
    filterState.rodzaj_rynku = params.get('rodzaj_rynku') || '';
    filterState.rok_od = parseTransactionRokOdFromUrl(params.get(TRANSACTION_ROK_OD_PARAM));
    filterState.rok_do = params.get('rok_do') || '';
    filterState.dzialka_id = params.get(DZIALKA_ID_PARAM) || '';
    filterState.listing_category = params.get('listing_category') || 'plot';
    filterState.listing_offer_type = params.get('listing_offer_type') || '';
    filterState.listing_location_query = normalizeAddress(params.get('listing_location_query') || '');
    filterState.listing_location_ref_type = normalizeListingLocationRefType(params.get('listing_location_ref_type') || '');
    filterState.listing_location_ref_id = params.get('listing_location_ref_id') || '';
    filterState.listing_location_ref_lat = normalizeCoordinate(params.get('listing_location_ref_lat') || '');
    filterState.listing_location_ref_lng = normalizeCoordinate(params.get('listing_location_ref_lng') || '');
    filterState.listing_location_ref_bbox = normalizeBbox(params.get('listing_location_ref_bbox') || '');
    filterState.listing_radius_km = isListingAreaReference(filterState.listing_location_ref_type)
        ? ''
        : normalizeRadiusKm(params.get('listing_radius_km') || '');
    filterState.listing_market_type = params.get('listing_market_type') || '';
    filterState.listing_advertiser_type = params.get('listing_advertiser_type') || '';
    filterState.listing_price_min = params.get('listing_price_min') || '';
    filterState.listing_price_max = params.get('listing_price_max') || '';
    filterState.listing_price_per_m2_min = params.get('listing_price_per_m2_min') || '';
    filterState.listing_price_per_m2_max = params.get('listing_price_per_m2_max') || '';
    filterState.listing_area_min = params.get('listing_area_min') || '';
    filterState.listing_area_max = params.get('listing_area_max') || '';
    filterState.listing_rooms = (params.get('listing_rooms') || '').split(',').filter(Boolean);
    filterState.listing_floor = (params.get('listing_floor') || '').split(',').filter(Boolean);
    filterState.listing_build_year_min = params.get('listing_build_year_min') || '';
    filterState.listing_build_year_max = params.get('listing_build_year_max') || '';
    filterState.listing_building_floors_min = params.get('listing_building_floors_min') || '';
    filterState.listing_building_floors_max = params.get('listing_building_floors_max') || '';
    filterState.listing_building_type = (params.get('listing_building_type') || '').split(',').filter(Boolean);
    filterState.listing_building_material = (params.get('listing_building_material') || '').split(',').filter(Boolean);
    filterState.listing_features = (params.get('listing_features') || '').split(',').filter(Boolean);
    filterState.listing_has_photos = params.get('listing_has_photos') === '1' ? '1' : '';
    filterState.listing_ownership_type = params.get('listing_ownership_type') || '';
    filterState.listing_condition = params.get('listing_condition') || '';
    filterState.listing_heating_type = params.get('listing_heating_type') || '';
    filterState.listing_energy_class = params.get('listing_energy_class') || '';
    filterState.listing_parking = params.get('listing_parking') || '';
    filterState.listing_rental_term = params.get('listing_rental_term') || '';
    filterState.listing_furnished = params.get('listing_furnished') === '1' ? '1' : '';
    filterState.listing_deposit_max = normalizeNonNegativeNumber(params.get('listing_deposit_max') || '');
    filterState.listing_pet_policy = params.get('listing_pet_policy') || '';
    filterState.listing_created_days = params.get('listing_created_days') || '';
    filterState.listing_description_query = normalizeAddress(params.get('listing_description_query') || '');
    filterState.listing_plot_zoning = params.get('listing_plot_zoning') || '';
    filterState.listing_road_access = params.get('listing_road_access') || '';
    filterState.listing_mpzp = params.get('listing_mpzp') || '';
    filterState.gunb_rok_wplywu_od = parseGunbRokWplywuOdFromUrl(params.get(GUNB_ROK_WPLYWU_OD_PARAM));
    filterState.bdot10k_impact_classification = normalizeBdot10kImpactClassification(params.get(BDOT10K_IMPACT_CLASSIFICATION_PARAM));
    filterState.bdot10k_nuisance_level_min = normalizeBdot10kScore(params.get(BDOT10K_NUISANCE_LEVEL_PARAM));
    filterState.bdot10k_usefulness_level_min = normalizeBdot10kScore(params.get(BDOT10K_USEFULNESS_LEVEL_PARAM));

    var selectedLatLng = null;
    if (selectedTid || selectedDid || selectedBid || selectedGunbId || selectedListingId || selectedDeveloperInvestmentId || selectedOtoczenieId || selectedSignalContext || selectedNoiseContext || openBdotId || (pendingNearbyRestore && isFinite(nearbyLat) && isFinite(nearbyLng))) {
        if (selectedNoiseContext) {
            selectedLatLng = selectedNoiseContext.latlng;
        } else if (isFinite(nearbyLat) && isFinite(nearbyLng)) {
            selectedLatLng = { lat: nearbyLat, lng: nearbyLng };
        } else if (!isNaN(lat) && !isNaN(lng)) {
            selectedLatLng = { lat: lat, lng: lng };
        }
    }

    var selectedSearchItem = null;
    var searchType = params.get('search_type');
    if (['locality', 'street', 'place', 'address', 'bdot_area'].indexOf(searchType) !== -1) {
        var sLat = parseFloat(params.get('search_lat'));
        var sLng = parseFloat(params.get('search_lng'));
        if (isFinite(sLat) && isFinite(sLng)) {
            selectedSearchItem = {
                type: searchType,
                id: params.get('search_id') || null,
                lat: sLat,
                lng: sLng,
                nazwa: params.get('search_name') || '',
                bbox: params.get('search_bbox') || null,
            };
        }
    }

    return {
        initState: {
            lat: !isNaN(lat) ? lat : DEFAULT_LAT,
            lng: !isNaN(lng) ? lng : DEFAULT_LNG,
            zoom: !isNaN(zoom) ? zoom : DEFAULT_ZOOM
        },
        currentAssetType: currentAssetType,
        currentSort: currentSort,
        activeTab: activeTab,
        selectedTid: selectedTid,
        selectedDid: selectedDid,
        selectedBid: selectedBid,
        selectedGunbId: selectedGunbId,
        selectedListingId: selectedListingId,
        selectedDeveloperInvestmentId: selectedDeveloperInvestmentId,
        selectedOtoczenieId: selectedOtoczenieId,
        selectedNoiseContext: selectedNoiseContext,
        browseLatlng: browseLatlng,
        activeNearbyId: activeNearbyId,
        pendingNearbyRestore: pendingNearbyRestore,
        nearbyLoaded: nearbyLoaded,
        selectedSignalContext: selectedSignalContext,
        selectedSearchItem: selectedSearchItem,
        parcelMeasurementsState: parseParcelMeasurementsState(params.get(PARCEL_MEASUREMENTS_PARAM)),
        filterState: filterState,
        selectedLatLng: selectedLatLng,
        layerState: {
            transakcjeDesired: transakcjeDesired,
            transakcjeActive: transakcjeActive,
            orthoDesired: urlLayers.indexOf('ortho') !== -1,
            mpzpDesired: urlLayers.indexOf('mpzp') !== -1,
            mpzpRuDesired: urlLayers.indexOf('mpzp-ru') !== -1,
            pogDesired: urlLayers.indexOf('pog') !== -1,
            studiumDesired: urlLayers.indexOf('studium') !== -1,
            powodzDesired: urlLayers.indexOf('powodz') !== -1,
            gesutDesired: urlLayers.indexOf('gesut') !== -1,
            portPolskaDesired: urlLayers.indexOf('port-polska') !== -1,
            sidusisDesired: urlLayers.indexOf('sidusis') !== -1,
            dzialkiDesired: urlLayers.indexOf('no-dzialki') === -1,
            dzialkiActive: urlLayers.indexOf('no-dzialki') === -1,
            budynkiDesired: urlLayers.indexOf('budynki') !== -1,
            budynkiActive: urlLayers.indexOf('budynki') !== -1,
            gunbDesired: urlLayers.indexOf('gunb') !== -1,
            gunbActive: urlLayers.indexOf('gunb') !== -1,
            sygnalyDesired: urlLayers.indexOf('no-sygnaly') === -1,
            sygnalyActive: urlLayers.indexOf('no-sygnaly') === -1,
            ogloszeniaDesired: urlLayers.indexOf('ogloszenia') !== -1,
            ogloszeniaActive: urlLayers.indexOf('ogloszenia') !== -1,
            ofertyDeweloperowDesired: urlLayers.indexOf('ceny-ofertowe') !== -1,
            ofertyDeweloperowActive: urlLayers.indexOf('ceny-ofertowe') !== -1,
            otoczenieDesired: urlLayers.indexOf('otoczenie') !== -1,
            otoczenieActive: urlLayers.indexOf('otoczenie') !== -1
        }
    };
}

export function applyFilterInputs(state) {
    assetEls.forEach(function(el) {
        el.classList.toggle('is-active', el.getAttribute('data-asset') === state.currentAssetType);
    });
    sortEls.forEach(function(el) {
        var isActive = el.getAttribute('data-sort') === sortField(state.currentSort);
        el.classList.toggle('is-active', isActive);
        if (isActive) el.setAttribute('data-dir', sortDir(state.currentSort).toLowerCase());
        else el.removeAttribute('data-dir');
    });
    if (addressInput) addressInput.value = state.filterState.address_query || '';
    if (addressRadiusKmInput) addressRadiusKmInput.value = state.filterState.address_radius_km || '';
    if (addressSuggestEl) {
        addressSuggestEl.hidden = true;
        addressSuggestEl.innerHTML = '';
    }
    if (wojInput) wojInput.value = state.filterState.woj || '';
    if (terytInput) terytInput.value = state.filterState.teryt || '';
    if (rodzajInput) rodzajInput.value = state.filterState.tran_rodzaj_trans || '';
    if (rynekInput) rynekInput.value = state.filterState.rodzaj_rynku || '';
    if (listingCategoryInput) listingCategoryInput.value = state.filterState.listing_category || '';
    if (listingOfferTypeInput) listingOfferTypeInput.value = state.filterState.listing_offer_type || '';
    if (listingLocationQueryInput) listingLocationQueryInput.value = state.filterState.listing_location_query || '';
    if (listingRadiusKmInput) {
        listingRadiusKmInput.disabled = isListingAreaReference(state.filterState.listing_location_ref_type);
        listingRadiusKmInput.value = listingRadiusKmInput.disabled ? '' : state.filterState.listing_radius_km || '1';
    }
    if (listingMarketTypeInput) listingMarketTypeInput.value = state.filterState.listing_market_type || '';
    if (listingAdvertiserTypeInput) listingAdvertiserTypeInput.value = state.filterState.listing_advertiser_type || '';
    if (listingPriceMinInput) listingPriceMinInput.value = state.filterState.listing_price_min || '';
    if (listingPriceMaxInput) listingPriceMaxInput.value = state.filterState.listing_price_max || '';
    if (listingPricePerM2MinInput) listingPricePerM2MinInput.value = state.filterState.listing_price_per_m2_min || '';
    if (listingPricePerM2MaxInput) listingPricePerM2MaxInput.value = state.filterState.listing_price_per_m2_max || '';
    if (listingAreaMinInput) listingAreaMinInput.value = state.filterState.listing_area_min || '';
    if (listingAreaMaxInput) listingAreaMaxInput.value = state.filterState.listing_area_max || '';
    if (listingRoomsInput) setSelectedOptions(listingRoomsInput, state.filterState.listing_rooms || []);
    if (listingFloorInput) setSelectedOptions(listingFloorInput, state.filterState.listing_floor || []);
    if (listingBuildYearMinInput) listingBuildYearMinInput.value = state.filterState.listing_build_year_min || '';
    if (listingBuildYearMaxInput) listingBuildYearMaxInput.value = state.filterState.listing_build_year_max || '';
    if (listingBuildingFloorsMinInput) listingBuildingFloorsMinInput.value = state.filterState.listing_building_floors_min || '';
    if (listingBuildingFloorsMaxInput) listingBuildingFloorsMaxInput.value = state.filterState.listing_building_floors_max || '';
    if (listingBuildingTypeInput) setSelectedOptions(listingBuildingTypeInput, state.filterState.listing_building_type || []);
    if (listingBuildingMaterialInput) setSelectedOptions(listingBuildingMaterialInput, state.filterState.listing_building_material || []);
    if (listingFeaturesInput) setSelectedOptions(listingFeaturesInput, state.filterState.listing_features || []);
    if (listingHasPhotosInput) listingHasPhotosInput.checked = state.filterState.listing_has_photos === '1';
    if (listingOwnershipTypeInput) listingOwnershipTypeInput.value = state.filterState.listing_ownership_type || '';
    if (listingConditionInput) listingConditionInput.value = state.filterState.listing_condition || '';
    if (listingHeatingTypeInput) listingHeatingTypeInput.value = state.filterState.listing_heating_type || '';
    if (listingEnergyClassInput) listingEnergyClassInput.value = state.filterState.listing_energy_class || '';
    if (listingParkingInput) listingParkingInput.value = state.filterState.listing_parking || '';
    if (listingRentalTermInput) listingRentalTermInput.value = state.filterState.listing_rental_term || '';
    if (listingFurnishedInput) listingFurnishedInput.checked = state.filterState.listing_furnished === '1';
    if (listingDepositMaxInput) listingDepositMaxInput.value = state.filterState.listing_deposit_max || '';
    if (listingPetPolicyInput) listingPetPolicyInput.value = state.filterState.listing_pet_policy || '';
    if (listingCreatedDaysInput) listingCreatedDaysInput.value = state.filterState.listing_created_days || '';
    if (listingDescriptionQueryInput) listingDescriptionQueryInput.value = state.filterState.listing_description_query || '';
    if (listingPlotZoningInput) listingPlotZoningInput.value = state.filterState.listing_plot_zoning || '';
    if (listingRoadAccessInput) listingRoadAccessInput.value = state.filterState.listing_road_access || '';
    if (listingMpzpInput) listingMpzpInput.value = state.filterState.listing_mpzp || '';
    if (rokOdInput) rokOdInput.value = state.filterState.rok_od || '';
    if (rokDoInput) rokDoInput.value = state.filterState.rok_do || '';
    if (dzialkaIdInput) dzialkaIdInput.value = state.filterState.dzialka_id || '';
    if (gunbRokWplywuOdInput) gunbRokWplywuOdInput.value = state.filterState.gunb_rok_wplywu_od || '';
    if (bdot10kImpactClassificationInput) bdot10kImpactClassificationInput.value = state.filterState.bdot10k_impact_classification || '';
    if (bdot10kNuisanceLevelInput) bdot10kNuisanceLevelInput.value = state.filterState.bdot10k_nuisance_level_min || '';
    if (bdot10kUsefulnessLevelInput) bdot10kUsefulnessLevelInput.value = state.filterState.bdot10k_usefulness_level_min || '';
    state.onSyncTerytHint();
}

export function readFilterInputs(state) {
    var nextFilterState = Object.assign({}, state.filterState);
    var previousAddressQuery = state.filterState.address_query || '';
    nextFilterState.address_query = normalizeAddress(addressInput && addressInput.value ? addressInput.value : '');
    nextFilterState.address_radius_km = normalizeRadiusKm(addressRadiusKmInput && addressRadiusKmInput.value ? addressRadiusKmInput.value : '');
    syncInputValue(addressRadiusKmInput, nextFilterState.address_radius_km);
    if (nextFilterState.address_query !== previousAddressQuery) {
        nextFilterState.address_ref_type = '';
        nextFilterState.address_ref_id = '';
        nextFilterState.address_ref_lat = '';
        nextFilterState.address_ref_lng = '';
        nextFilterState.address_ref_bbox = '';
    }
    if (!nextFilterState.address_query) {
        nextFilterState.address_radius_km = '';
        syncInputValue(addressRadiusKmInput, '');
    }
    nextFilterState.woj = wojInput && wojInput.value ? wojInput.value : '';
    nextFilterState.teryt = terytInput && terytInput.value ? state.parseTerytFilterValue(terytInput.value) : '';
    nextFilterState.tran_rodzaj_trans = rodzajInput && rodzajInput.value ? rodzajInput.value : '';
    nextFilterState.rodzaj_rynku = rynekInput && rynekInput.value ? rynekInput.value : '';
    nextFilterState.listing_category = listingCategoryInput && listingCategoryInput.value ? listingCategoryInput.value : '';
    nextFilterState.listing_offer_type = listingOfferTypeInput && listingOfferTypeInput.value ? listingOfferTypeInput.value : '';
    var previousListingLocationQuery = state.filterState.listing_location_query || '';
    nextFilterState.listing_location_query = normalizeAddress(listingLocationQueryInput && listingLocationQueryInput.value ? listingLocationQueryInput.value : '');
    nextFilterState.listing_radius_km = normalizeRadiusKm(listingRadiusKmInput && listingRadiusKmInput.value ? listingRadiusKmInput.value : '');
    syncInputValue(listingRadiusKmInput, nextFilterState.listing_radius_km);
    if (nextFilterState.listing_location_query !== previousListingLocationQuery) {
        nextFilterState.listing_location_ref_type = '';
        nextFilterState.listing_location_ref_id = '';
        nextFilterState.listing_location_ref_lat = '';
        nextFilterState.listing_location_ref_lng = '';
        nextFilterState.listing_location_ref_bbox = '';
    }
    if (!nextFilterState.listing_location_query || isListingAreaReference(nextFilterState.listing_location_ref_type)) {
        nextFilterState.listing_radius_km = '';
        syncInputValue(listingRadiusKmInput, '');
    }
    nextFilterState.listing_market_type = listingMarketTypeInput && listingMarketTypeInput.value ? listingMarketTypeInput.value : '';
    nextFilterState.listing_advertiser_type = listingAdvertiserTypeInput && listingAdvertiserTypeInput.value ? listingAdvertiserTypeInput.value : '';
    nextFilterState.listing_price_min = normalizeNonNegativeNumber(listingPriceMinInput && listingPriceMinInput.value ? listingPriceMinInput.value : '');
    nextFilterState.listing_price_max = normalizeNonNegativeNumber(listingPriceMaxInput && listingPriceMaxInput.value ? listingPriceMaxInput.value : '');
    nextFilterState.listing_price_per_m2_min = normalizeNonNegativeNumber(listingPricePerM2MinInput && listingPricePerM2MinInput.value ? listingPricePerM2MinInput.value : '');
    nextFilterState.listing_price_per_m2_max = normalizeNonNegativeNumber(listingPricePerM2MaxInput && listingPricePerM2MaxInput.value ? listingPricePerM2MaxInput.value : '');
    nextFilterState.listing_area_min = normalizeNonNegativeNumber(listingAreaMinInput && listingAreaMinInput.value ? listingAreaMinInput.value : '');
    nextFilterState.listing_area_max = normalizeNonNegativeNumber(listingAreaMaxInput && listingAreaMaxInput.value ? listingAreaMaxInput.value : '');
    nextFilterState.listing_rooms = selectedOptions(listingRoomsInput);
    nextFilterState.listing_floor = selectedOptions(listingFloorInput);
    nextFilterState.listing_build_year_min = normalizeNonNegativeNumber(listingBuildYearMinInput && listingBuildYearMinInput.value ? listingBuildYearMinInput.value : '');
    nextFilterState.listing_build_year_max = normalizeNonNegativeNumber(listingBuildYearMaxInput && listingBuildYearMaxInput.value ? listingBuildYearMaxInput.value : '');
    nextFilterState.listing_building_floors_min = normalizeNonNegativeNumber(listingBuildingFloorsMinInput && listingBuildingFloorsMinInput.value ? listingBuildingFloorsMinInput.value : '');
    nextFilterState.listing_building_floors_max = normalizeNonNegativeNumber(listingBuildingFloorsMaxInput && listingBuildingFloorsMaxInput.value ? listingBuildingFloorsMaxInput.value : '');
    nextFilterState.listing_building_type = selectedOptions(listingBuildingTypeInput);
    nextFilterState.listing_building_material = selectedOptions(listingBuildingMaterialInput);
    nextFilterState.listing_features = selectedOptions(listingFeaturesInput);
    nextFilterState.listing_has_photos = listingHasPhotosInput && listingHasPhotosInput.checked ? '1' : '';
    nextFilterState.listing_ownership_type = listingOwnershipTypeInput && listingOwnershipTypeInput.value ? listingOwnershipTypeInput.value : '';
    nextFilterState.listing_condition = listingConditionInput && listingConditionInput.value ? listingConditionInput.value : '';
    nextFilterState.listing_heating_type = listingHeatingTypeInput && listingHeatingTypeInput.value ? listingHeatingTypeInput.value : '';
    nextFilterState.listing_energy_class = listingEnergyClassInput && listingEnergyClassInput.value ? listingEnergyClassInput.value : '';
    nextFilterState.listing_parking = listingParkingInput && listingParkingInput.value ? listingParkingInput.value : '';
    nextFilterState.listing_rental_term = listingRentalTermInput && listingRentalTermInput.value ? listingRentalTermInput.value : '';
    nextFilterState.listing_furnished = listingFurnishedInput && listingFurnishedInput.checked ? '1' : '';
    nextFilterState.listing_deposit_max = normalizeNonNegativeNumber(listingDepositMaxInput && listingDepositMaxInput.value ? listingDepositMaxInput.value : '');
    nextFilterState.listing_pet_policy = listingPetPolicyInput && listingPetPolicyInput.value ? listingPetPolicyInput.value : '';
    nextFilterState.listing_created_days = listingCreatedDaysInput && listingCreatedDaysInput.value ? listingCreatedDaysInput.value : '';
    nextFilterState.listing_description_query = normalizeAddress(listingDescriptionQueryInput && listingDescriptionQueryInput.value ? listingDescriptionQueryInput.value : '');
    nextFilterState.listing_plot_zoning = listingPlotZoningInput && listingPlotZoningInput.value ? listingPlotZoningInput.value : '';
    nextFilterState.listing_road_access = listingRoadAccessInput && listingRoadAccessInput.value ? listingRoadAccessInput.value : '';
    nextFilterState.listing_mpzp = listingMpzpInput && listingMpzpInput.value ? listingMpzpInput.value : '';
    nextFilterState.rok_od = normalizeTransactionRokOd(rokOdInput && rokOdInput.value ? rokOdInput.value : '');
    nextFilterState.rok_do = rokDoInput && rokDoInput.value ? String(rokDoInput.value).trim() : '';
    nextFilterState.dzialka_id = dzialkaIdInput && dzialkaIdInput.value ? dzialkaIdInput.value.trim() : '';
    nextFilterState.gunb_rok_wplywu_od = normalizeGunbRokWplywuOd(gunbRokWplywuOdInput && gunbRokWplywuOdInput.value ? gunbRokWplywuOdInput.value : '');
    nextFilterState.bdot10k_impact_classification = normalizeBdot10kImpactClassification(
        bdot10kImpactClassificationInput && bdot10kImpactClassificationInput.value ? bdot10kImpactClassificationInput.value : ''
    );
    nextFilterState.bdot10k_nuisance_level_min = normalizeBdot10kScore(
        bdot10kNuisanceLevelInput && bdot10kNuisanceLevelInput.value ? bdot10kNuisanceLevelInput.value : ''
    );
    nextFilterState.bdot10k_usefulness_level_min = normalizeBdot10kScore(
        bdot10kUsefulnessLevelInput && bdot10kUsefulnessLevelInput.value ? bdot10kUsefulnessLevelInput.value : ''
    );
    if (terytInput && nextFilterState.teryt) terytInput.value = nextFilterState.teryt;
    state.onSyncTerytHint();
    return nextFilterState;
}

export function getSharedFilters(state) {
    var filters = { asset: state.currentAssetType, sort: state.currentSort };
    Object.keys(state.filterState).forEach(function(key) {
        if (key === TRANSACTION_ROK_OD_PARAM) {
            filters[key] = state.filterState[key] || YEAR_FILTER_EMPTY_URL_VALUE;
            return;
        }
        if (key === GUNB_ROK_WPLYWU_OD_PARAM) {
            filters[key] = state.filterState[key] || GUNB_EMPTY_FILTER_URL_VALUE;
            return;
        }
        if (Array.isArray(state.filterState[key])) {
            if (state.filterState[key].length) filters[key] = state.filterState[key].join(',');
            return;
        }
        if (state.filterState[key]) filters[key] = state.filterState[key];
    });
    return filters;
}

export function getApiFilters(state) {
    return {
        asset_type: state.currentAssetType,
        address_query: state.filterState.address_query || undefined,
        address_radius_km: state.filterState.address_ref_lat && state.filterState.address_ref_lng
            ? state.filterState.address_radius_km || undefined
            : undefined,
        address_ref_type: state.filterState.address_ref_type || undefined,
        address_ref_id: state.filterState.address_ref_id || undefined,
        address_ref_lat: state.filterState.address_ref_lat || undefined,
        address_ref_lng: state.filterState.address_ref_lng || undefined,
        woj: state.filterState.woj || undefined,
        teryt: state.filterState.teryt || undefined,
        obszar: state.filterState.obszar || undefined,
        tran_rodzaj_trans: state.filterState.tran_rodzaj_trans || undefined,
        rodzaj_rynku: state.filterState.rodzaj_rynku || undefined,
        rok_od: state.filterState.rok_od || YEAR_FILTER_EMPTY_URL_VALUE,
        rok_do: state.filterState.rok_do || undefined
    };
}

export function syncUrl(state) {
    if (!state.map) return;

    var mapState = {
        center: state.centerOverride || state.map.getCenter(),
        zoom: state.zoomOverride != null ? state.zoomOverride : state.mapZoom()
    };
    var params = getSharedFilters(state);

    if (PERF_DEBUG) params.perf = '1';
    if (PARCEL_RENDER_MODE === 'vector') params.parcel_mode = 'vector';
    params.lat = mapState.center.lat.toFixed(5);
    params.lng = mapState.center.lng.toFixed(5);
    params.zoom = mapState.zoom;

    if (state.activeTab !== 'list') params.tab = state.activeTab;
    if (state.selectedTid) params.tid = state.selectedTid;
    if (state.selectedDid) params.did = state.selectedDid;
    if (state.selectedBid) params.bid = state.selectedBid;
    if (state.selectedGunbId) params.gid = state.selectedGunbId;
    if (state.selectedListingId) params.listing_id = state.selectedListingId;
    if (state.selectedDeveloperInvestmentId) params.developer_offer_investment_id = state.selectedDeveloperInvestmentId;
    if (state.selectedOtoczenieId) params.otoczenie_id = state.selectedOtoczenieId;
    if (state.selectedNoiseContext && state.selectedNoiseContext.id
        && state.selectedLatLng && isFinite(state.selectedLatLng.lat) && isFinite(state.selectedLatLng.lng)) {
        params.noise_hit = state.selectedNoiseContext.id;
        params.noise_hit_lat = Number(state.selectedLatLng.lat).toFixed(6);
        params.noise_hit_lng = Number(state.selectedLatLng.lng).toFixed(6);
        if (state.selectedNoiseContext.level != null && isFinite(state.selectedNoiseContext.level)) {
            params.noise_hit_level = Number(state.selectedNoiseContext.level);
        }
    }
    if (state.browseLatlng && isFinite(state.browseLatlng.lat) && isFinite(state.browseLatlng.lng)) {
        params.browse_lat = Number(state.browseLatlng.lat).toFixed(6);
        params.browse_lng = Number(state.browseLatlng.lng).toFixed(6);
    }
    if (state.selectedSearchItem) {
        var s = state.selectedSearchItem;
        params.search_type = s.type;
        if (s.id != null && s.id !== '') params.search_id = String(s.id);
        if (isFinite(s.lat)) params.search_lat = Number(s.lat).toFixed(6);
        if (isFinite(s.lng)) params.search_lng = Number(s.lng).toFixed(6);
        if (s.nazwa) params.search_name = s.nazwa;
        if (Array.isArray(s.bbox) && s.bbox.length === 4) {
            params.search_bbox = s.bbox.join(',');
        } else if (typeof s.bbox === 'string' && s.bbox) {
            params.search_bbox = s.bbox;
        }
    }
    if (state.selectedSignalContext && state.selectedSignalContext.entityType && state.selectedSignalContext.entityId != null) {
        params.signal_type = state.selectedSignalContext.entityType;
        params.signal_entity_id = String(state.selectedSignalContext.entityId);
        if (state.selectedSignalContext.selectedSignalId) params.signal_id = state.selectedSignalContext.selectedSignalId;
        if (state.selectedSignalContext.targetIdentifier) params.signal_target_identifier = state.selectedSignalContext.targetIdentifier;
        if (state.selectedSignalContext.targetLabel) params.signal_target_label = state.selectedSignalContext.targetLabel;
        if (state.selectedSignalContext.targetSubtitle) params.signal_target_subtitle = state.selectedSignalContext.targetSubtitle;
    }

    var hasNearbyState = state.nearbyLoaded || state.pendingNearbyRestore || !!state.activeNearbyId;
    if (hasNearbyState) params.nearby = '1';
    if (hasNearbyState && state.selectedLatLng && isFinite(state.selectedLatLng.lat) && isFinite(state.selectedLatLng.lng)) {
        params.nearby_lat = Number(state.selectedLatLng.lat).toFixed(6);
        params.nearby_lng = Number(state.selectedLatLng.lng).toFixed(6);
    }
    if (state.activeNearbyId) params.nearby_id = state.activeNearbyId;

    var parcelMeasurementsValue = encodeParcelMeasurementsState(state.parcelMeasurementsState);
    if (parcelMeasurementsValue) params[PARCEL_MEASUREMENTS_PARAM] = parcelMeasurementsValue;

    Object.assign(params, noiseLayerRuntime.getUrlParams());
    var activeLayers = [];
    if (kowrLayerRuntime.getDesired()) activeLayers.push('kowr');
    if (ownershipLayerRuntime.getDesired()) activeLayers.push('ownership');
    if (landUseLayerRuntime.getDesired()) activeLayers.push('land-use');
    if (meshCoverageRuntime.getDesired()) activeLayers.push('widoki3d');
    if (!state.desiredLayers.transakcjeDesired) activeLayers.push('no-transakcje');
    if (state.desiredLayers.orthoDesired) activeLayers.push('ortho');
    if (state.desiredLayers.mpzpDesired) activeLayers.push('mpzp');
    if (state.desiredLayers.mpzpRuDesired) activeLayers.push('mpzp-ru');
    if (state.desiredLayers.pogDesired) activeLayers.push('pog');
    if (state.desiredLayers.studiumDesired) activeLayers.push('studium');
    if (state.desiredLayers.powodzDesired) activeLayers.push('powodz');
    if (state.desiredLayers.gesutDesired) activeLayers.push('gesut');
    if (state.desiredLayers.portPolskaDesired) activeLayers.push('port-polska');
    if (state.desiredLayers.sidusisDesired) activeLayers.push('sidusis');
    if (educationLayerRuntime.getDesired()) activeLayers.push('szkoly');
    if (!state.desiredLayers.dzialkiDesired) activeLayers.push('no-dzialki');
    if (state.desiredLayers.budynkiDesired) activeLayers.push('budynki');
    if (state.desiredLayers.gunbDesired) activeLayers.push('gunb');
    if (!state.desiredLayers.sygnalyDesired) activeLayers.push('no-sygnaly');
    if (state.desiredLayers.ogloszeniaDesired) activeLayers.push('ogloszenia');
    if (state.desiredLayers.ofertyDeweloperowDesired) activeLayers.push('ceny-ofertowe');
    if (state.desiredLayers.otoczenieDesired) activeLayers.push('otoczenie');
    if (activeLayers.length) params.layers = activeLayers.join(',');

    var method = state.historyMode === 'push' ? 'pushState' : 'replaceState';
    history[method]({ rcnMapState: true }, '', BASE_URL + '?' + qstr(params));
}
